/* eslint-disable no-shadow */
import React, { useEffect, FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { loadAgencyMetrics } from "../../actions/dashboardActions";
import WidgetLoading from "../shared/loaders/WidgetLoading";
import agencyMetricsType from "./types/agencyMetricsType";
import {
  Wrapper,
  Header,
  MemberSince,
  Body,
  Row,
  Column,
  Number,
  Description,
  InfoDot,
} from "./styles/agency-performance-metrics-styles";

interface IAgencyPerformanceMetricsTypes {
  performance: {
    memberSince: string;
    interviewRate: number;
    averageCVs: number;
    engagements: number;
    submissions: number;
    interviews: number;
    placements: number;
  };
}

interface IAgencyPerformanceMetricsProps {
  loadAgencyMetrics: (recruiterId) => void;
  loading: boolean;
  agencyMetrics: IAgencyPerformanceMetricsTypes;
  recruiterId: number;
}

export const AgencyPerformanceMetrics: FunctionComponent<
  IAgencyPerformanceMetricsProps
> = ({ loadAgencyMetrics, loading, agencyMetrics, recruiterId }) => {
  useEffect(() => {
    loadAgencyMetrics({ recruiterId });
  }, []);

  const interviewRateTooltip =
    "<strong><u>Interview Rate</u></strong><br><br>Percentage of CV submissions to <br /> interview in your current membership";
  const avgCvsSubmittedTootlip =
    "<strong><u>Avg. CVs Submitted</u></strong><br><br>Average number of CV submissions per role";
  const engagementsTooltip =
    "<strong><u>Engagements</u></strong><br><br>Total engagements during your current <br /> membership with at least 1 CV submission";
  const submissionsTooltip =
    "<strong><u>Submissions</u></strong><br><br>Total number of submissions during <br /> your current membership";
  const interviewsTooltip =
    "<strong><u>Interviews</u></strong><br><br>Total number of confirmed interviews <br /> during your current membership";
  const placementsTooltip =
    "<strong><u>Placements</u></strong><br><br>Total number of placements during <br /> your current membership";

  return (
    <Wrapper>
      {loading && <WidgetLoading zIndex={1} />}
      <Header className="panel-heading">
        <div className="panel-title">Your Performance</div>
        <MemberSince>
          Member since {agencyMetrics && agencyMetrics.performance.memberSince}
        </MemberSince>
      </Header>
      <Body>
        <ReactTooltip
          html
          place="bottom"
          type="dark"
          effect="float"
          aria-haspopup="true"
          id="info-tooltip"
        />
        <Row>
          <Column
            flex={1}
            data-test-id="interview-rate"
            data-for="info-tooltip"
            data-tip={interviewRateTooltip}
          >
            <Number fontSize="large">
              {agencyMetrics && agencyMetrics.performance.interviewRate}%
              <InfoDot>
                <FontAwesomeIcon icon={faInfoCircle} />
              </InfoDot>
            </Number>
            <Description>Interview Rate</Description>
          </Column>
          <Column
            flex={1}
            data-test-id="avg-cv-submitted"
            data-for="info-tooltip"
            data-tip={avgCvsSubmittedTootlip}
          >
            <Number fontSize="large">
              {agencyMetrics && agencyMetrics.performance.averageCVs}
              <InfoDot>
                <FontAwesomeIcon icon={faInfoCircle} />
              </InfoDot>
            </Number>
            <Description>Average CVs Submitted</Description>
          </Column>
        </Row>
        <Row>
          <Column
            flex={4}
            data-test-id="engagements"
            data-tip={engagementsTooltip}
            data-for="info-tooltip"
          >
            <Number fontSize="medium">
              {agencyMetrics && agencyMetrics.performance.engagements}
            </Number>
            <FontAwesomeIcon icon={faInfoCircle} />
            <Description>Engagements</Description>
          </Column>
          <Column flex={1} noMobile>
            <FontAwesomeIcon icon={faChevronRight} />
          </Column>
          <Column
            flex={4}
            data-test-id="submissions"
            data-tip={submissionsTooltip}
            data-for="info-tooltip"
          >
            <Number fontSize="medium">
              {agencyMetrics && agencyMetrics.performance.submissions}
            </Number>
            <FontAwesomeIcon icon={faInfoCircle} />
            <Description>Submissions</Description>
          </Column>
          <Column flex={1} noMobile>
            <FontAwesomeIcon icon={faChevronRight} />
          </Column>
          <Column
            flex={4}
            data-test-id="interviews"
            data-tip={interviewsTooltip}
            data-for="info-tooltip"
          >
            <Number fontSize="medium">
              {agencyMetrics && agencyMetrics.performance.interviews}
            </Number>
            <FontAwesomeIcon icon={faInfoCircle} />
            <Description>Interviews</Description>
          </Column>
          <Column flex={1} noMobile>
            <FontAwesomeIcon icon={faChevronRight} />
          </Column>
          <Column
            flex={4}
            data-test-id="placements"
            data-tip={placementsTooltip}
            data-for="info-tooltip"
          >
            <Number fontSize="medium">
              {agencyMetrics && agencyMetrics.performance.placements}
            </Number>
            <FontAwesomeIcon icon={faInfoCircle} />
            <Description>Placements</Description>
          </Column>
        </Row>
      </Body>
    </Wrapper>
  );
};

AgencyPerformanceMetrics.propTypes = {
  loadAgencyMetrics: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  agencyMetrics: agencyMetricsType,
  recruiterId: PropTypes.number.isRequired,
};

AgencyPerformanceMetrics.defaultProps = {
  agencyMetrics: null,
};

interface IReduxState {
  dashboard: {
    loading: boolean;
    agencyMetrics: IAgencyPerformanceMetricsTypes;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  loading: state.dashboard.loading,
  agencyMetrics: state.dashboard.agencyMetrics,
});

export default connect(mapStateToProps, { loadAgencyMetrics })(
  AgencyPerformanceMetrics
);
